import { BrandLoader } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useEffect, useId, useState } from "react";
import * as Sentry from "@sentry/react";
import { useUpdateUser } from "../hooks/useUpdateUser";
import { DATABASE } from "../types/tables-data";
import { betaFeatures } from "../constans";

// Mapping for converting between "UI display name" and "database field name"
const featureMapping: Record<string, string> = {
  // "Legal Brief: Custom Templates": "legal_brief_templates",
  "Another Feature Name": "another_feature_variable"
  // add more if needed
};

interface BetaAccessModalProps {
  isOpen: boolean;
  userId: string | undefined;
  userName: string;
  closeModal: () => void;
}

const BetaAccessModal: React.FC<BetaAccessModalProps> = ({
  isOpen,
  userId,
  userName,
  closeModal
}) => {
  const { updateBetaFeatureAccess, getUserBetaFeatures } = useUpdateUser();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const idPrefix = useId();

  // Convert database format -> presentation
  const convertToPresentation = (features: string[]) =>
    features.map(
      (feature) =>
        Object.keys(featureMapping).find(
          (key) => featureMapping[key] === feature
        ) || feature // fallback to the raw feature if not found
    );

  // Convert presentation -> database format
  const convertToDatabase = (feature: string) =>
    featureMapping[feature] || feature;

  // Fetch user’s existing features on open
  useEffect(() => {
    const fetchBetaFeatures = async () => {
      if (!userId) return;
      setIsLoading(true);

      try {
        const featuresFromDb = await getUserBetaFeatures(
          userId,
          DATABASE.LAWYERS
        );
        setSelectedFeatures(convertToPresentation(featuresFromDb));
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchBetaFeatures();
    }
  }, [isOpen]);

  // Toggle an individual feature
  const handleFeatureToggle = async (feature: string, isChecked: boolean) => {
    if (!userId) return;
    setIsLoading(true);

    try {
      const featureToStore = convertToDatabase(feature);
      await updateBetaFeatureAccess(
        userId,
        DATABASE.LAWYERS,
        featureToStore,
        isChecked
      );

      if (isChecked) {
        setSelectedFeatures((prev) => [...prev, feature]);
      } else {
        setSelectedFeatures((prev) => prev.filter((f) => f !== feature));
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) closeModal();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Beta Features for {userName}</DialogTitle>
          <DialogDescription>
            Manage beta feature access for this user.
          </DialogDescription>
        </DialogHeader>

        {/* Show a spinner while loading initial data */}
        {isLoading && selectedFeatures.length === 0 ? (
          <BrandLoader size="md" label="Loading beta features" />
        ) : (
          <div className="flex flex-col gap-3">
            {betaFeatures.map((feature, index) => {
              const id = `${idPrefix}-${index}`;
              return (
                <div key={feature} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={selectedFeatures.includes(feature)}
                    onCheckedChange={(checked) =>
                      handleFeatureToggle(feature, checked === true)
                    }
                    disabled={isLoading /* Disable toggling while API call */}
                  />
                  <Label htmlFor={id} className="text-base font-normal">
                    {feature}
                  </Label>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button onClick={closeModal} disabled={isLoading}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BetaAccessModal;
